import type { FC } from 'react'

import { Icon, Tooltip } from '@skytech/ui'
import { Badge } from '@skytech/ui/Badge'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import InformationCircleIcon from 'mdi-react/InformationCircleIcon'
import { useSelector } from 'react-redux'

import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import Address from '@/components/primitives/Address'
import DataPoint, { DataPoints } from '@/components/primitives/DataPoint'
import { EcomOrderContact } from '@/components/shop/orders/order/EcomOrderContact'
import {
  getEcomOrderContactPerson,
  getEcomOrderDealerAddress,
  getEcomOrderDealerCity,
  getEcomOrderDealerZip,
  getEcomOrderDeliveryAddressIsPrivate,
  getEcomOrderIsCustomAddress,
  getEcomOrderProductsUser,
  getEcomOrderUser,
} from '@/ducks/ecomOrderReducer'
import { getFormatterMobileNumber } from '@/util/formatters/text'

const EcomOrderUser = () => {
  const { whiteLabel } = useAppContext()
  const translate = useTranslation()
  const orderUser = useSelector(getEcomOrderUser)
  const productsUser = useSelector(getEcomOrderProductsUser)
  const address = useSelector(getEcomOrderDealerAddress)
  const zip = useSelector(getEcomOrderDealerZip)
  const city = useSelector(getEcomOrderDealerCity)
  const isCustomAddress = useSelector(getEcomOrderIsCustomAddress)
  const contactPerson = useSelector(getEcomOrderContactPerson)
  const deliveryAddressIsPrivate = useSelector(getEcomOrderDeliveryAddressIsPrivate)

  return (
    <section className="user-details">
      <h3>
        <Translation id="User" />
      </h3>

      <DataPoints>
        <DataPoint
          header={<Translation id="Ordered by" />}
          value={<User user={orderUser || productsUser} />}
        />

        <DataPoint
          header={
            <div>
              <Translation id="Delivery address" />

              {isCustomAddress && (
                <>
                  <Icon
                    size="small"
                    className="absolute -right-5 -top-px"
                    whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                  >
                    <InformationCircleIcon id="custom-address" />
                  </Icon>
                  <Tooltip anchorId="custom-address" content={translate('Custom address')} />
                </>
              )}
            </div>
          }
          value={
            <div className="flex flex-col gap-2">
              <Address street1={address} zip={zip} city={city} />
              <Badge
                type="info"
                className="lowercase"
                whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
              >
                {deliveryAddressIsPrivate ? (
                  <Translation id="Private" />
                ) : (
                  <Translation id="Business" />
                )}
              </Badge>
            </div>
          }
        />

        {contactPerson && (
          <DataPoint
            header={<Translation id="Contact Person" />}
            value={
              <EcomOrderContact
                name={contactPerson?.name}
                email={contactPerson?.email}
                phone={contactPerson?.phone}
              />
            }
          />
        )}

        {productsUser && (
          <DataPoint
            header={<Translation id="End user" />}
            value={<User user={productsUser} showAccountingCode />}
          />
        )}
      </DataPoints>
    </section>
  )
}

const User: FC<{
  user: ReturnType<typeof getEcomOrderProductsUser> | ReturnType<typeof getEcomOrderUser>
  showAccountingCode?: boolean
}> = ({ user, showAccountingCode }) => {
  if (!user) return null

  const { user_name, phone_number, email } = user
  const emailParts = email?.split('@')

  return (
    <>
      {user_name}
      {'department_name' in user && user.department_name && (
        <div className="department"> {`${user.department_name} (${user.department_code})`} </div>
      )}
      {emailParts && (
        <div className="mt-0.5 text-sm">
          {emailParts[0]}
          <wbr />@{emailParts[1]}
        </div>
      )}

      {phone_number && (
        <div className="mt-0.5 text-sm">{getFormatterMobileNumber(phone_number)}</div>
      )}

      {showAccountingCode && 'accounting_code' in user && user.accounting_code && (
        <div className="mt-0.5 text-sm">
          <Translation id="Accounting code" /> {user.accounting_code}
        </div>
      )}
    </>
  )
}

export default EcomOrderUser
